import { verifySchoolCubeToken } from "@/api/common";
import useLayoutNavigate from "@/hooks/useLayoutNavigate";
import { getFromSessionStorage, setToLocalStorage, setToSessionStorage } from "@/lib/storage";
import { showSuccessToast } from "@/lib/toast";
import { Tenant } from "@/types/tenants.types";
import { User } from "@/types/user.types";
import { useMutation } from "@tanstack/react-query";
import React, {
  createContext,
  useEffect,
  useState,
  Dispatch,
  SetStateAction,
} from "react";
import { useSearchParams } from "react-router-dom";

type AuthContextType = {
  isAuthenticated: boolean;
  user?: User | null;
  token?: string | null;
  school?: Tenant | null;
  setUser: Dispatch<SetStateAction<User | null>>;
  setToken: Dispatch<SetStateAction<string | null>>;
  setSchool: Dispatch<SetStateAction<Tenant | null>>;
  logout: () => void;
  currentTermId: string | undefined;
  setCurrentTermId: Dispatch<string | undefined>;
  currentSessionId: string | undefined;
  setCurrentSessionId: Dispatch<string | undefined>;
};

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

const AuthContextProvider = ({ children }: { children: React.ReactNode }) => {
  const layoutNavigate = useLayoutNavigate();
  const [searchParams] = useSearchParams()

  const [user, setUser] = useState(() => getFromSessionStorage("user"));
  const [token, setToken] = useState(() => getFromSessionStorage("token"));
  const [school, setSchool] = useState(() => getFromSessionStorage("school"));
  const [currentTermId, setCurrentTermId] = useState(() => getFromSessionStorage("currentTermId"));
  const [currentSessionId, setCurrentSessionId] = useState(() => getFromSessionStorage("currentSessionId"));

  useEffect(() => {
    setToSessionStorage("currentSessionId", currentSessionId);
    setToSessionStorage("currentTermId", currentTermId);
    setToSessionStorage("user", user);
    setToSessionStorage("token", token);
    setToSessionStorage("school", school);
  }, [user, token, school, currentTermId, currentSessionId]);

  function logout() {
    setUser(null);
    setToken(null);
    layoutNavigate("/login");
    showSuccessToast("Logout successful!");
  }

  const { mutate: verifySchoolCubeTokenMutate } = useMutation(verifySchoolCubeToken, {
    onSuccess: ({ message, data }) => {
      const { user, token, school, current_term, current_session, type } = data!;

      setToLocalStorage("code", school.path);
      sessionStorage.setItem("token", JSON.stringify(token));
      sessionStorage.setItem("user", JSON.stringify(user));
      sessionStorage.setItem("school", JSON.stringify(school));
      sessionStorage.setItem("currentSessionId", current_session);
      sessionStorage.setItem("currentTermId", current_term);
      setToken(token);
      setUser(user);
      setSchool(school);
      setCurrentSessionId(current_session);
      setCurrentTermId(current_term);

      showSuccessToast(message || "Login successful!", {
        description: `Welcome back, ${user.firstname}!`,
        duration: 2000,
        closeButton: true,
      });

      if (type === "teacher") {
        window.location.href = "/teacher"
      } else if (type === "admin") {
        window.location.href = "/admin"
      } else {
        window.location.href = "/"
      }
    },
  })

  useEffect(() => {
    if (searchParams.has("call") && searchParams.has("token")) {
      const redirectToken = searchParams.get("token");
      verifySchoolCubeTokenMutate({ token: redirectToken })
    }
  }, [searchParams, verifySchoolCubeTokenMutate])

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!user && !!token,
        token,
        setToken,
        setUser,
        user,
        logout,
        school,
        setSchool,
        currentTermId,
        setCurrentTermId,
        currentSessionId,
        setCurrentSessionId
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContextProvider;
